import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import { getCurrentUserIsGuest } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import Button, { ButtonIcon } from "@insite/mobius/Button";
import Clickable from "@insite/mobius/Clickable";
import GridContainer from "@insite/mobius/GridContainer";
import Hidden from "@insite/mobius/Hidden";
import Link from "@insite/mobius/Link/Link";
import LoadingOverlay from "@insite/mobius/LoadingOverlay";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import Pagination from "@insite/mobius/Pagination";
import Typography from "@insite/mobius/Typography";
import LeftButton from "@store/Components/Icons/LeftButton";
import RightButton from "@store/Components/Icons/RightButton";
import StorePartProductContext from "@store/Components/Store/StoreSchematicsView/StorePartProductContext";
import styles, { DotButton, SchematicLazyImage } from "@store/Components/Store/StoreSchematicsView/styles";
import { DOCUMENT_TYPE } from "@store/constants";
import GridItem from "@store/MobiusOverrides/GridItem/GridItem";
import { getShowNormalPrice, getShowRetailPrice } from "@store/Store/Context/ContextSelectors";
import { getPartsViewModel } from "@store/Store/Data/Parts/PartsSelectors";
import addParameter from "@store/Store/Pages/ProductDetails/Parts/Handlers/AddParameter";
import { getProductDetailsPartsDataView } from "@store/Store/Pages/ProductDetails/Parts/ProductDetailsPartsSelectors";
import StoreApplicationState from "@store/Store/StoreApplicationState";
import { PartViewModel } from "@store/Types/Parts";
import { useEmblaCarousel } from "embla-carousel/react";
import React, { useEffect, useMemo, useState } from "react";
import { connect, ResolveThunks } from "react-redux";

// Poolcorp Custom Code - Start
import StorePartsActions from "@store/Components/Store/StoreSchematicsView/StorePartsActions";
// Poolcorp Custom Code - End

type Props = HasProductContext & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const mapStateToProps = (state: StoreApplicationState) => {
    const stateContext = state.store.pages.productDetailsParts;
    const isReady = stateContext.isReady;

    let partsDataView;
    let parts;

    if (isReady) {
        partsDataView = getProductDetailsPartsDataView(state);
        parts = getPartsViewModel(state, partsDataView);
    }

    return {
        isReady,
        parts,
        isLoading: stateContext.isLoading,
        pagination: partsDataView?.pagination,
        isSignedIn: state.context.session.userName && !getCurrentUserIsGuest(state),
        rememberMe: state.context.session.rememberMe,
        showNormalPrice: getShowNormalPrice(state),
        showRetailPrice: getShowRetailPrice(state),
    };
};

const mapDispatchToProps = {
    addParameter,
};

const SchematicsView: React.FC<Props> = ({
    productContext: { product },
    addParameter,
    isReady,
    isLoading,
    isSignedIn,
    rememberMe,
    parts,
    pagination,
    showNormalPrice,
    showRetailPrice,
}) => {
    const { id: productId, documents } = product;
    const [emblaRef, embla] = useEmblaCarousel();
    const [scrollSnaps, setScrollSnaps] = useState<number[]>();
    const [emblaCurrentIndex, setEmblaCurrentIndex] = useState<number>(0);

    const schematics = useMemo(() => {
        return documents ? documents.filter((document: any) => document.documentType === DOCUMENT_TYPE.schematics) : [];
    }, [documents]);

    useEffect(() => {
        if (embla) {
            embla.reInit({ align: "center" });
            setScrollSnaps(embla.scrollSnapList());
            embla.on("select", () => {
                setEmblaCurrentIndex(embla.selectedScrollSnap());
            });
        }
    }, [embla]);

    const changePage = (page: number) => {
        addParameter({ page });
    };

    const changeResultsPerPage = (event: React.ChangeEvent<HTMLSelectElement>) => {
        addParameter({ pageSize: parseInt(event.currentTarget.value, 10) });
    };

    if (!isReady) {
        return (
            <StyledWrapper {...styles.loadingSpinner}>
                <LoadingSpinner />
            </StyledWrapper>
        );
    }

    if (!schematics.length && !parts?.length) {
        return (
            <Typography {...styles.noResults}>{translate("There are no parts or schematics available.")}</Typography>
        );
    }

    const changeSlide = (event: React.ChangeEvent<HTMLLinkElement>, index: number) => {
        if (embla && index !== emblaCurrentIndex) {
            event.preventDefault();
            embla.scrollTo(index);
        }
    };


    return (
        <>
            {schematics.length > 0 && (
                <StyledWrapper {...styles.schematicsWrapper}>
                    {schematics.length >= 2 ? (
                        <>
                            <Button
                                {...styles.prevArrowButton}
                                onClick={() => embla && embla.scrollPrev()}
                                disabled={emblaCurrentIndex === 0}
                                data-test-selector="prevBtn"
                            >
                                <ButtonIcon src={LeftButton} />
                            </Button>
                            <StyledWrapper>
                                <StyledWrapper ref={emblaRef} {...styles.carousel}>
                                    <StyledWrapper {...styles.carouselList}>
                                        {schematics.map((schematic: any, index: number) => {
                                            return (
                                                <StyledWrapper key={schematic.id} {...styles.carouselItem}>
                                                    <Clickable
                                                        {...styles.schematicLink}
                                                        href={schematic.filePath}
                                                        target="_blank"
                                                        onClick={(event: any) => changeSlide(event, index)}
                                                    >
                                                        <SchematicLazyImage
                                                            selected={index === emblaCurrentIndex}
                                                            src={schematic.filePath}
                                                            altText={schematic.name}
                                                        />
                                                    </Clickable>
                                                </StyledWrapper>
                                            );
                                        })}
                                    </StyledWrapper>
                                </StyledWrapper>
                                {scrollSnaps && (
                                    <StyledWrapper {...styles.dotsWrapper}>
                                        {scrollSnaps.map((scrollSnap, index) => {
                                            return (
                                                <DotButton
                                                    key={scrollSnap}
                                                    selected={index === emblaCurrentIndex}
                                                    onClick={() => embla && embla.scrollTo(index)}
                                                />
                                            );
                                        })}
                                    </StyledWrapper>
                                )}
                            </StyledWrapper>
                            <Button
                                {...styles.nextArrowButton}
                                disabled={emblaCurrentIndex === schematics.length - 1}
                                onClick={() => embla && embla.scrollNext()}
                                data-test-selector="nextBtn"
                            >
                                <ButtonIcon src={RightButton} />
                            </Button>
                        </>
                    ) : (
                        <>
                            {schematics.map((schematic: any) => {
                                return (
                                    <StyledWrapper key={schematic.id} {...styles.carouselItem}>
                                        <Clickable href={schematic.filePath} target="_blank" {...styles.schematicLink}>
                                            <SchematicLazyImage
                                                single={true}
                                                src={schematic.filePath}
                                                altText={schematic.name}
                                            />
                                        </Clickable>
                                    </StyledWrapper>
                                );
                            })}
                        </>
                    )}
                </StyledWrapper>
            )}
            {parts && parts.length > 0 && (
                <>
                    <LoadingOverlay loading={isLoading} {...styles.loadingOverlay}>
                        <StyledWrapper {...styles.container}>
                            <GridContainer {...styles.tableGridHead}>
                                <GridItem {...styles.legendHeaderGridCell}></GridItem>
                                <GridItem {...styles.manufacturerNumberHeaderGridCell}>
                                    <Typography {...styles.tableGridHeaderText}>
                                        {translate("Store_MfgNumber")}
                                    </Typography>
                                </GridItem>
                                <GridItem {...styles.productNumberHeaderGridCell}>
                                    <Typography {...styles.tableGridHeaderText}>
                                        {translate("Product #")}
                                    </Typography>
                                </GridItem>
                                <GridItem {...styles.descriptionHeaderGridCell}>
                                    <Typography {...styles.tableGridHeaderText}>
                                        {translate("Description")}
                                    </Typography>
                                </GridItem>
                                <GridItem {...styles.notesHeaderGridCell}>
                                    <Typography {...styles.tableGridHeaderText}>
                                        {translate("Notes")}
                                    </Typography>
                                </GridItem>
                                <GridItem {...styles.availabilityHeaderGridCell}>
                                    {(isSignedIn || rememberMe) && (
                                        <Typography {...styles.tableGridHeaderText}>
                                            {translate("Availability")}
                                        </Typography>
                                    )}
                                </GridItem>
                                <GridItem {...styles.normalPriceHeaderGridCell}>
                                    {((isSignedIn || rememberMe) && showNormalPrice) && (
                                        <Typography {...styles.tableGridHeaderText}>
                                            {translate("Price")}
                                        </Typography>
                                    )}
                                </GridItem>
                                <GridItem {...styles.retailPriceHeaderGridCell}>
                                    {((isSignedIn || rememberMe) && showRetailPrice) && (
                                        <Typography {...styles.tableGridHeaderText}>
                                            {translate("Retail Price")}
                                        </Typography>
                                    )}
                                </GridItem>
                            </GridContainer>
                            <GridContainer {...styles.tableGridBody}>
                                {parts.map((part: PartViewModel) => (
                                    <StorePartProductContext product={part.product} key={part.key}>
                                        <GridItem {...styles.tableGridRow} key={part.key}>
                                            <GridContainer {...styles.tableGridRowExternalContainer}>
                                                <GridItem {...styles.mobileLegendBodyGridCell}>
                                                    <Typography {...styles.mobileLegendText}>
                                                        {part.legend}
                                                    </Typography>
                                                </GridItem>
                                                <GridItem {...styles.tableGridInnerRow}>
                                                    <GridContainer {...styles.tableGridRowContainer}>
                                                        <GridItem {...styles.legendBodyGridCell}>
                                                            <Typography>
                                                                {part.legend}
                                                            </Typography>
                                                        </GridItem>
                                                        <GridItem {...styles.manufacturerNumberBodyGridCell}>
                                                            <Typography>
                                                                {part.manufacturerItem}
                                                            </Typography>
                                                        </GridItem>
                                                        <GridItem {...styles.productNumberBodyGridCell}>
                                                            <Typography>
                                                                {part.productNumber}
                                                            </Typography>
                                                        </GridItem>
                                                        <GridItem {...styles.descriptionBodyGridCell}>
                                                            {part.product ? (
                                                                <Link href={part.product.canonicalUrl}>
                                                                    {part.product.productTitle}
                                                                </Link>
                                                            ) : (
                                                                part.description
                                                            )}
                                                        </GridItem>
                                                        <GridItem {...styles.mobileProductNumberBodyGridCell}>
                                                            <Typography {...styles.mobileHeader}>
                                                                {`${translate("Product #")}: `}
                                                            </Typography>
                                                            <Typography>
                                                                {part.productNumber}
                                                            </Typography>
                                                        </GridItem>
                                                        <GridItem {...styles.mobileManufacturerNumberBodyGridCell}>
                                                            <Typography {...styles.mobileHeader}>
                                                                {`${translate("Store_MfgNumber")}: `}
                                                            </Typography>
                                                            <Typography>
                                                                {part.manufacturerItem}
                                                            </Typography>
                                                        </GridItem>
                                                        <GridItem {...styles.notesBodyGridCell}>
                                                            {part.notes && (
                                                                <>
                                                                    <Hidden {...styles.mobileHeaderHidden}>
                                                                        <Typography {...styles.mobileHeader}>
                                                                            {`${translate("Notes")}: `}
                                                                        </Typography>
                                                                    </Hidden>
                                                                    <Typography>
                                                                        {part.notes}
                                                                    </Typography>
                                                                </>
                                                            )}
                                                        </GridItem>
                                                        {part.product && <StorePartsActions />}
                                                    </GridContainer>
                                                </GridItem>
                                            </GridContainer>
                                        </GridItem>
                                    </StorePartProductContext>
                                )) }
                            </GridContainer>
                        </StyledWrapper>
                    </LoadingOverlay>
                    {(isSignedIn || rememberMe) && parts.some(x => !x.product) && (
                        <Typography {...styles.globalNote}>
                            {translate("*Please contact local branch to inquire about this item.")}
                        </Typography>
                    )}
                    {pagination && (
                        <Pagination
                            {...styles.pagination}
                            resultsCount={pagination.totalItemCount}
                            currentPage={pagination.page}
                            resultsPerPage={pagination.pageSize}
                            resultsPerPageOptions={pagination.pageSizeOptions}
                            onChangePage={changePage}
                            onChangeResultsPerPage={changeResultsPerPage}
                        />
                    )}
                </>
            )}
        </>
    );
};

export default withProductContext(connect(mapStateToProps, mapDispatchToProps)(SchematicsView));
